import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  withCredentials: true, // Send cookies
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Don't set Content-Type for FormData (multipart)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
let _refreshPromise = null; // deduplicate concurrent refresh calls

api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Only attempt token refresh for 401s on real protected endpoints.
    // Skip for: refresh endpoint itself, /me check endpoints (they fail silently),
    // and requests that have already been retried.
    const isAuthCheck = originalRequest.url?.endsWith('/me') || originalRequest.url?.endsWith('/instructor-auth/me');
    const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/instructor-auth/refresh');
    const isLoginEndpoint = originalRequest.url?.endsWith('/auth/login') || originalRequest.url?.endsWith('/instructor-auth/login');
    
    if (error.response?.status === 401 && 
        !originalRequest._retry && 
        !isRefreshEndpoint &&
        !isAuthCheck &&
        !isLoginEndpoint) {
      originalRequest._retry = true;
      
      try {
        // Deduplicate: if another request already kicked off a refresh, reuse it
        if (!_refreshPromise) {
          // Use the correct refresh endpoint based on the failing request URL
          const isInstructorRoute = originalRequest.url?.includes('instructor-auth') ||
            window.location.pathname.startsWith('/instructor-portal');
          const refreshEndpoint = isInstructorRoute ? '/instructor-auth/refresh' : '/auth/refresh';
          _refreshPromise = api.post(refreshEndpoint).finally(() => {
            _refreshPromise = null;
          });
        }
        await _refreshPromise;
        return api(originalRequest);
      } catch (refreshError) {
        // Only redirect to admin login if the user is already in the admin area
        if (window.location.pathname.startsWith('/ft-portal') &&
            window.location.pathname !== '/ft-portal/login') {
          window.location.href = '/ft-portal/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    // Extract error message
    const errorMessage = error.response?.data?.error || 
                        error.response?.data?.message ||
                        error.message ||
                        'An error occurred';
    
    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || error.response?.data?.detail
    });
  }
);

// API methods

// Auth
export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  getCurrentAdmin: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { currentPassword, newPassword }),
  createAdmin: (email, password, role) => 
    api.post('/auth/create-admin', { email, password, role })
};

// Applications
export const applications = {
  submit: (formData) => api.post('/applications/submit', formData),
  getAll: (params) => api.get('/applications', { params }),
  getOne: (id) => api.get(`/applications/${id}`),
  updateStatus: (id, status, admin_notes, rejection_reason) => 
    api.patch(`/applications/${id}/status`, { status, admin_notes, rejection_reason }),
  delete: (id) => api.delete(`/applications/${id}`),
  getStats: () => api.get('/applications/stats')
};

// Instructors
export const instructors = {
  getAll: (params) => api.get('/instructors', { params }),
  getOne: (id) => api.get(`/instructors/${id}`),
  update: (id, data) => api.put(`/instructors/${id}`, data),
  completeFirstSession: (id) => api.post(`/instructors/${id}/complete-first-session`),
  delete: (id) => api.delete(`/instructors/${id}`)
};

// Test Centres
export const testCentres = {
  getAll: (params) => api.get('/test-centres', { params }),
  getOne: (id) => api.get(`/test-centres/${id}`),
  getRegions: () => api.get('/test-centres/regions'),
  create: (data) => api.post('/test-centres', data),
  update: (id, data) => api.put(`/test-centres/${id}`, data),
  delete: (id) => api.delete(`/test-centres/${id}`)
};

// CMS
export const cms = {
  getContent: (pageSlug) => api.get(`/cms/${pageSlug}`),
  getAllContent: () => api.get('/cms'),
  updateContent: (pageSlug, data) => api.put(`/cms/${pageSlug}`, data),
  getVersionHistory: (pageSlug) => api.get(`/cms/${pageSlug}/versions`),
  getVersion: (pageSlug, version) => api.get(`/cms/${pageSlug}/versions/${version}`),
  revertToVersion: (pageSlug, version) => api.post(`/cms/${pageSlug}/revert/${version}`)
};

// Referrals
export const referrals = {
  getAll: (params) => api.get('/referrals', { params }),
  getStats: () => api.get('/referrals/stats'),
  updateStatus: (referral_id, status, admin_notes) => 
    api.post('/referrals/update-status', { referral_id, status, admin_notes }),
  bulkProcess: (referral_ids) => api.post('/referrals/bulk-process', { referral_ids }),
  exportReport: (params) => api.get('/referrals/export', { 
    params, 
    responseType: 'blob' 
  })
};

// Payments
export const payments = {
  createIntent: (data) => api.post('/payments/create-intent', data),
  getAll: (params) => api.get('/payments', { params }),
  getStats: () => api.get('/payments/stats'),
  processRefund: (payment_id, amount, reason) => 
    api.post('/payments/refund', { payment_id, amount, reason })
};

export default api;
